import React, { forwardRef, useState } from 'react';
import classNames from 'classnames';
import styles from './StyledInput.module.css';

export interface StyledInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Input variant style
   */
  variant?: 'outlined' | 'filled' | 'standard';
  /**
   * Input size
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Icon to display on the left side
   */
  startIcon?: React.ReactNode;
  /**
   * Icon to display on the right side
   */
  endIcon?: React.ReactNode;
  /**
   * Label text
   */
  label?: string;
  /**
   * Helper text displayed below the input
   */
  helperText?: string;
  /**
   * Error state
   */
  error?: boolean;
  /**
   * Error message to display
   */
  errorMessage?: string;
  /**
   * Full width
   */
  fullWidth?: boolean;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Container CSS classes
   */
  containerClassName?: string;
}

const StyledInput = forwardRef<HTMLInputElement, StyledInputProps>(
  (
    {
      variant = 'outlined',
      size = 'medium',
      startIcon,
      endIcon,
      label,
      helperText,
      error = false,
      errorMessage,
      fullWidth = false,
      className,
      containerClassName,
      disabled = false,
      value,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasStartIcon = !!startIcon;
    const hasEndIcon = !!endIcon;
    const displayHelperText = error ? errorMessage : helperText;

    // For outlined variant, label should float when focused or has value
    const hasValue = value !== undefined ? String(value).length > 0 : false;
    const shouldFloatLabel = variant === 'outlined' && (isFocused || hasValue);
    const shouldHideLabel = variant === 'outlined' && isFocused;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      props.onBlur?.(e);
    };

    return (
      <div
        className={classNames(
          styles.container,
          {
            [styles.fullWidth]: fullWidth,
            [styles.disabled]: disabled,
          },
          containerClassName
        )}
      >
        {/* Label for filled and standard variants (outside input) */}
        {label && variant !== 'outlined' && (
          <label
            className={classNames(styles.label, styles[size], {
              [styles.error]: error,
              [styles.disabled]: disabled,
            })}
            htmlFor={props.id}
          >
            {label}
          </label>
        )}

        <div
          className={classNames(styles.inputContainer, styles[variant], styles[size], {
            [styles.error]: error,
            [styles.disabled]: disabled,
            [styles.focused]: isFocused,
            [styles.hasStartIcon]: hasStartIcon,
            [styles.hasEndIcon]: hasEndIcon,
            [styles.hasValue]: hasValue,
          })}
        >
          {startIcon && (
            <div className={classNames(styles.icon, styles.startIcon)}>{startIcon}</div>
          )}

          {/* Floating label for outlined variant (inside input) */}
          {label && variant === 'outlined' && (
            <label
              className={classNames(styles.floatingLabel, styles[size], {
                [styles.error]: error,
                [styles.disabled]: disabled,
                [styles.floating]: shouldFloatLabel,
                [styles.hidden]: shouldHideLabel,
                [styles.hasStartIcon]: hasStartIcon,
              })}
              htmlFor={props.id}
            >
              {label}
            </label>
          )}

          <input
            ref={ref}
            className={classNames(
              styles.input,
              styles[variant],
              styles[size],
              {
                [styles.hasStartIcon]: hasStartIcon,
                [styles.hasEndIcon]: hasEndIcon,
              },
              className
            )}
            disabled={disabled}
            value={value}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />

          {endIcon && <div className={classNames(styles.icon, styles.endIcon)}>{endIcon}</div>}
        </div>

        {displayHelperText && (
          <div
            className={classNames(styles.helperText, {
              [styles.error]: error,
            })}
          >
            {displayHelperText}
          </div>
        )}
      </div>
    );
  }
);

StyledInput.displayName = 'StyledInput';

export default StyledInput;
